import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { useViewer, useViewerHasPermission } from "@/auth/shared";
import { button } from "@/components/base/button";
import { Link } from "@/components/base/link";
import { subtitle, title } from "@/components/base/primitives";
import { VenueDisplay } from "@/components/venues/display";
import { VenueHeader } from "@/components/venues/header";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";

export const Route = createFileRoute("/venues/$venueId")({
  ssr: "data-only",
  loader: async ({ params: { venueId }, context: { queryClient, trpc } }) => {
    return await queryClient.ensureQueryData(
      trpc.venues.get.queryOptions({
        id: Number.parseInt(venueId, 10),
      }),
    );
  },
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const trpc = useTRPC();

  const { data: venue } = useSuspenseQuery(
    trpc.venues.get.queryOptions({
      id: Number.parseInt(params.venueId, 10),
    }),
  );

  const hasEditPermission = useViewerHasPermission({
    venues: ["update"],
  });

  const viewer = useViewer();

  const isViewerDirector =
    viewer &&
    venue.directors.some(
      ({ director }) => director.profile.userId === viewer.id,
    );

  const canUpdate =
    hasEditPermission &&
    (isViewerDirector ||
      (viewer && ["admin", "superadmin"].includes(viewer.role)));

  return (
    <DefaultLayout classNames={{ content: "bg-white" }}>
      <VenueHeader {...venue} canUpdate={canUpdate} />

      <div className="border-b-2 border-gray-300 py-16 text-center">
        <h1 className={title()}>{venue.name}</h1>
        <h2 className={subtitle({ class: "font-bold mb-4" })}>{venue.city}</h2>
        <Link
          to="/tournaments"
          search={{ venues: [venue.id] }}
          className={button({ radius: "full", color: "primary" })}
        >
          View Tournaments
        </Link>
      </div>

      <VenueDisplay venue={venue} />
    </DefaultLayout>
  );
}
